import { useServices } from '@/app/providers/ServiceContext';
import { Button } from '@/core/ui/components/Button';
import { useToast } from '@/app/providers';
import { useDateFormatter } from '@/shared/hooks';
import type {
  TenantBillingSummary,
  TenantBillingPlan,
  TenantBillingInvoice,
  TenantPaymentMethod,
  TenantPremiumModule,
} from '@yawi/types';
import {
  AlertCircle,
  Check,
  ChevronRight,
  CreditCard,
  Crown,
  Download,
  HardDrive,
  Loader2,
  Package,
  Plus,
  Receipt,
  Settings,
  Star,
  Trash2,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

// Helper functions
const formatCurrency = (amount: number, currency = 'MXN') => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
  }).format(amount);
};

// formatDate will be provided by useDateFormatter hook

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
    case 'PAID':
      return 'bg-emerald-100 text-emerald-700';
    case 'TRIAL':
      return 'bg-blue-100 text-blue-700';
    case 'PENDING':
      return 'bg-amber-100 text-amber-700';
    case 'PAST_DUE':
    case 'OVERDUE':
      return 'bg-red-100 text-red-700';
    case 'SUSPENDED':
    case 'CANCELLED':
      return 'bg-slate-100 text-slate-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const getProgressColor = (percentage: number) => {
  if (percentage >= 90) return 'bg-red-500';
  if (percentage >= 70) return 'bg-amber-500';
  return 'bg-emerald-500';
};

// Sub-components
interface UsageBarProps {
  label: string;
  used: number;
  limit: number;
  percentage: number;
  unit?: string;
  icon: React.ReactNode;
}

const UsageBar: React.FC<UsageBarProps> = ({ label, used, limit, percentage, unit = '', icon }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2 text-slate-600">
        {icon}
        <span>{label}</span>
      </div>
      <span className="text-slate-500">
        {used.toLocaleString()}{unit} / {limit.toLocaleString()}{unit}
      </span>
    </div>
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${getProgressColor(percentage)}`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
    <div className="text-xs text-slate-400 text-right">{percentage}% usado</div>
  </div>
);

interface BillingTabProps {
  canUpdate?: boolean;
  onChangePlan?: () => void;
  onAddPaymentMethod?: () => void;
  onViewInvoice?: (invoice: TenantBillingInvoice) => void;
  onPayInvoice?: (invoice: TenantBillingInvoice) => void;
}

export const BillingTab: React.FC<BillingTabProps> = ({
  canUpdate = false,
  onChangePlan,
  onAddPaymentMethod,
  onViewInvoice,
  onPayInvoice,
}) => {
  const { billingRepo } = useServices();
  const toast = useToast();
  const { formatDate } = useDateFormatter();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<TenantBillingSummary | null>(null);
  const [plans, setPlans] = useState<TenantBillingPlan[]>([]);
  const [invoices, setInvoices] = useState<TenantBillingInvoice[]>([]);
  const [modules, setModules] = useState<TenantPremiumModule[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<TenantPaymentMethod[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load data function
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [summaryData, plansData, invoicesData, modulesData, paymentMethodsData] = await Promise.all([
        billingRepo.getBillingSummary(),
        billingRepo.getAvailablePlans(),
        billingRepo.getInvoices(),
        billingRepo.getAvailableModules(),
        billingRepo.getPaymentMethods(),
      ]);
      setSummary(summaryData);
      setPlans(plansData);
      setInvoices(invoicesData);
      setModules(modulesData);
      setPaymentMethods(paymentMethodsData);
    } catch (err) {
      console.error('Error loading billing data:', err);
      setError('Error al cargar la informacion de facturacion');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [billingRepo]);

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    if (!canUpdate) return;
    try {
      await billingRepo.setDefaultPaymentMethod(paymentMethodId);
      const methods = await billingRepo.getPaymentMethods();
      setPaymentMethods(methods);
      toast.success('Metodo de pago predeterminado actualizado');
    } catch {
      toast.error('Error al actualizar el metodo de pago');
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    if (!canUpdate) return;
    try {
      await billingRepo.deletePaymentMethod(paymentMethodId);
      const methods = await billingRepo.getPaymentMethods();
      setPaymentMethods(methods);
      toast.success('Metodo de pago eliminado');
    } catch {
      toast.error('Error al eliminar el metodo de pago');
    }
  };

  const handleToggleModule = async (moduleCode: string, isActive: boolean) => {
    if (!canUpdate) return;
    try {
      if (isActive) {
        await billingRepo.removeModules([moduleCode]);
        toast.success('Modulo desactivado');
      } else {
        await billingRepo.addModules([moduleCode]);
        toast.success('Modulo activado');
      }
      // Reload summary
      const summaryData = await billingRepo.getBillingSummary();
      setSummary(summaryData);
    } catch {
      toast.error('Error al actualizar el modulo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <p className="text-slate-600">{error}</p>
        <Button variant="secondary" className="mt-4" onClick={loadData}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const subscription = summary.subscription;
  const usage = summary.usage;
  const pendingInvoices = summary.pendingInvoices ?? [];
  const currentPlan = summary.plan || subscription?.plan || plans.find(p => p.id === subscription?.planId);

  if (!subscription) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <CreditCard className="w-12 h-12 text-slate-300 mb-4" />
        <p className="text-slate-600">No hay suscripcion activa</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan Overview */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-5 h-5" />
              <span className="text-sm font-medium text-white/80">Tu plan actual</span>
            </div>
            <h3 className="text-2xl font-bold mb-1">{currentPlan?.name || 'Plan'}</h3>
            <p className="text-white/70 text-sm">{currentPlan?.description}</p>
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              subscription.status === 'ACTIVE' ? 'bg-white/20 text-white' : getStatusColor(subscription.status)
            }`}>
              {subscription.status === 'ACTIVE' ? 'Activo' :
               subscription.status === 'TRIAL' ? 'Prueba' :
               subscription.status === 'PAST_DUE' ? 'Pago pendiente' :
               subscription.status === 'SUSPENDED' ? 'Suspendido' : subscription.status}
            </span>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/20 flex items-center justify-between">
          <div>
            <p className="text-white/60 text-sm">Precio mensual</p>
            <p className="text-3xl font-bold">{formatCurrency(currentPlan?.basePrice || 0)}</p>
          </div>
          <div className="text-right">
            <p className="text-white/60 text-sm">Proxima facturacion</p>
            <p className="font-medium">{formatDate(subscription.nextBillingDate)}</p>
          </div>
        </div>

        {canUpdate && onChangePlan && (
          <Button
            variant="secondary"
            className="mt-4 w-full border-white/30 text-white hover:bg-white/10"
            onClick={onChangePlan}
          >
            <Settings className="w-4 h-4 mr-2" />
            Cambiar plan
          </Button>
        )}
      </div>

      {/* Pending Invoices Alert */}
      {pendingInvoices.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-amber-800">Facturas pendientes</h4>
              <p className="text-sm text-amber-600 mt-1">
                Tienes {pendingInvoices.length} factura(s) pendiente(s) de pago.
              </p>
              <div className="mt-3 space-y-2">
                {pendingInvoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between bg-white rounded-lg p-3">
                    <div>
                      <p className="font-medium text-slate-800">{invoice.invoiceNumber}</p>
                      <p className="text-sm text-slate-500">Vence: {formatDate(invoice.dueDate)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-slate-800">{formatCurrency(invoice.totalAmount)}</span>
                      {canUpdate && onPayInvoice && (
                        <Button size="sm" onClick={() => onPayInvoice(invoice as unknown as TenantBillingInvoice)}>
                          Pagar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Metrics */}
      {usage && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-500" />
              Uso del periodo actual
            </h4>
            <span className="text-sm text-slate-500">{usage.currentPeriod}</span>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <UsageBar
              label="Usuarios activos"
              used={usage.activeUsers}
              limit={usage.limits.users}
              percentage={usage.percentages.users}
              icon={<Users className="w-4 h-4" />}
            />
            <UsageBar
              label="Unidades"
              used={usage.totalUnits}
              limit={usage.limits.units}
              percentage={usage.percentages.units}
              icon={<Package className="w-4 h-4" />}
            />
            <UsageBar
              label="Almacenamiento"
              used={Math.round(usage.storageBytes / (1024 * 1024 * 1024) * 10) / 10}
              limit={Math.round(usage.limits.storageBytes / (1024 * 1024 * 1024) * 10) / 10}
              percentage={usage.percentages.storage}
              unit=" GB"
              icon={<HardDrive className="w-4 h-4" />}
            />
            <UsageBar
              label="Notificaciones"
              used={usage.notificationsSent}
              limit={usage.limits.notifications}
              percentage={usage.percentages.notifications}
              icon={<Zap className="w-4 h-4" />}
            />
          </div>

          {/* Overages warning */}
          {(usage.overages.users > 0 || usage.overages.units > 0 || usage.overages.storageBytes > 0) && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Has excedido los limites de tu plan. Se aplicaran cargos adicionales en tu proxima factura.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Payment Methods */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h4 className="font-bold text-slate-800 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary-500" />
            Metodos de pago
          </h4>
          {canUpdate && onAddPaymentMethod && (
            <Button variant="secondary" size="sm" onClick={onAddPaymentMethod}>
              <Plus className="w-4 h-4 mr-1" />
              Agregar
            </Button>
          )}
        </div>

        {paymentMethods.length === 0 ? (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No tienes metodos de pago configurados</p>
            {canUpdate && onAddPaymentMethod && (
              <Button variant="secondary" className="mt-4" onClick={onAddPaymentMethod}>
                Agregar metodo de pago
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-8 bg-white rounded flex items-center justify-center border">
                    {method.cardBrand === 'visa' && <span className="text-blue-600 font-bold text-xs">VISA</span>}
                    {method.cardBrand === 'mastercard' && <span className="text-red-600 font-bold text-xs">MC</span>}
                    {method.cardBrand === 'amex' && <span className="text-blue-800 font-bold text-xs">AMEX</span>}
                    {!['visa', 'mastercard', 'amex'].includes(method.cardBrand || '') && (
                      <CreditCard className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{method.label}</p>
                    {method.expMonth && method.expYear && (
                      <p className="text-sm text-slate-500">Vence {method.expMonth}/{method.expYear}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {method.isDefault ? (
                    <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded">
                      Predeterminado
                    </span>
                  ) : (
                    canUpdate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefaultPaymentMethod(method.id)}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Predeterminar
                      </Button>
                    )
                  )}
                  {canUpdate && !method.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeletePaymentMethod(method.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Premium Modules */}
      {modules.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="font-bold text-slate-800 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Modulos premium
            </h4>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {modules.map((module) => {
              const isActive = subscription.activeModules.includes(module.code);
              return (
                <div
                  key={module.id}
                  className={`p-4 rounded-xl border-2 transition-colors ${
                    isActive ? 'border-primary-500 bg-primary-50' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h5 className="font-semibold text-slate-800">{module.name}</h5>
                      <p className="text-sm text-slate-500 mt-1">{module.description}</p>
                    </div>
                    {canUpdate && (
                      <button
                        type="button"
                        onClick={() => handleToggleModule(module.code, isActive)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          isActive ? 'bg-primary-500' : 'bg-slate-200'
                        }`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            isActive ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  <p className="text-lg font-bold text-slate-800">
                    {formatCurrency(module.monthlyPrice)}<span className="text-sm font-normal text-slate-500">/mes</span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invoice History */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h4 className="font-bold text-slate-800 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary-500" />
            Historial de facturas
          </h4>
        </div>

        {invoices.length === 0 ? (
          <div className="text-center py-8">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No hay facturas aun</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-slate-500 border-b">
                  <th className="pb-3 font-medium">Factura</th>
                  <th className="pb-3 font-medium">Periodo</th>
                  <th className="pb-3 font-medium">Estado</th>
                  <th className="pb-3 font-medium text-right">Monto</th>
                  <th className="pb-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.slice(0, 10).map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-slate-50">
                    <td className="py-3">
                      <span className="font-medium text-slate-800">{invoice.invoiceNumber}</span>
                    </td>
                    <td className="py-3 text-slate-600">{invoice.period}</td>
                    <td className="py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {invoice.status === 'PAID' ? 'Pagada' :
                         invoice.status === 'PENDING' ? 'Pendiente' :
                         invoice.status === 'OVERDUE' ? 'Vencida' :
                         invoice.status === 'CANCELLED' ? 'Cancelada' : invoice.status}
                      </span>
                    </td>
                    <td className="py-3 text-right font-medium text-slate-800">
                      {formatCurrency(invoice.totalAmount, invoice.currency)}
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {onViewInvoice && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewInvoice(invoice)}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        )}
                        {invoice.pdfUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(invoice.pdfUrl!, '_blank')}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        {canUpdate && invoice.status === 'PENDING' && onPayInvoice && (
                          <Button size="sm" onClick={() => onPayInvoice(invoice)}>
                            Pagar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingTab;
